import React, { useCallback } from 'react';
import { FormSpy } from 'react-final-form';
import Form from 'js/components/common/Form';
import { Button } from 'js/components/common/Button';
import { useFormatter } from 'js/hooks/useFormatter';
import { map, identity, path, prop, equals } from 'ramda';
import { Select } from 'js/components/common/Fields/Select';
import { useUserSelector, useAppSelector } from 'js/store/hooks';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import EditableDropDown from 'js/components/common/Fields/EditableDropDown';
import { WarningMessage } from 'js/components/common/Fields/WarningMessage';
import { usePortletConfig, usePortletSelector, usePortletTranslations } from 'js/components/system/Portlet';

import Hint from '../Hint';
import * as helpers from './helpers';
import { PERIODS } from './constants';
import { PendingLimits } from '../PendingLimits';
import { saveLimits, getLimitType } from '../server';
import { useOnSubmit, useGetAscendingOrderLimitError } from '../hooks';
import { useValidationConfig, useEddConfig, useInitialValues, useGetPendingLimitText } from './hooks';

import style from './index.scss';

export const useSaveLimits = () => {
    const availablePeriods = usePortletSelector(path(['config', 'availablePeriods']));
    const { portletType: type } = usePortletConfig();

    return useCallback((data, form) => {
        const { initialValues } = form.getState();

        const {
            limits,
            action
        } = helpers.prepareLimitsForSave(initialValues, data, availablePeriods);

        return saveLimits({ type, limits, action });
    }, [availablePeriods, type]);
};

export const LimitsForm = () => {
    const {
        actualLimits, pendingLimits, fetchPortlet,
        config: { availablePeriods }
    } = usePortletSelector(identity);
    const formatter = useFormatter();
    const t = usePortletTranslations();
    const initialValues = useInitialValues();
    const { portletType, tkPrefix } = usePortletConfig();
    const validationConfig = useValidationConfig();
    const getPendingLimitText = useGetPendingLimitText();
    const currencyCode = useUserSelector(prop('currencyCode'));
    const betLossLimitActions = useAppSelector(prop('betLossLimitActions'));
    const { mode, dataList, displayValuesMap } = useEddConfig();
    const getAscendingOrderLimitError = useGetAscendingOrderLimitError({
        fields: [...availablePeriods], exceptions: [0]
    });

    const limitType = getLimitType(portletType);
    const hasPendingLimits = Object.keys(pendingLimits).length > 0;
    const typeLimitActions = betLossLimitActions[`${limitType}limitActions`];
    const limitActions = helpers.getLimitActions(typeLimitActions);
    const isLimitActionHidden = limitActions.length < 2;
    const isUserLimitActionAllowed = helpers.getUserLimitAction(actualLimits) === initialValues.action;

    const isPristine = (data) => {
        if (!isUserLimitActionAllowed && isLimitActionHidden) {
            return false;
        }

        return equals(data, initialValues);
    };

    const onSubmit = useOnSubmit({
        isPristine,
        fetchLimits: fetchPortlet,
        saveLimits: useSaveLimits()
    });

    const parseDataList = item => item.displayValueTKey
        ? { ...item, displayValue: t(item.displayValueTKey) }
        : item;
    const parsedPendingLimits = PERIODS
        .filter(period => Boolean(pendingLimits[period]))
        .map(period => ({
            ...pendingLimits[period],
            date: pendingLimits[period].startDate,
            text: getPendingLimitText(pendingLimits[period])
        }));

    return (
        <Form
            onSubmit={onSubmit}
            validateOnBlur={true}
            initialValues={initialValues}
            validationConfig={validationConfig}
            fieldsToReplaceComma={availablePeriods}
            render={({ submitting, pristine, submitError, handleSubmit }) => (
                <form
                    className={style.form}
                    onSubmit={event => event.preventDefault()}
                >
                    <h2 className={style.title}>{t('title.limits')}</h2>
                    <FormSpy subscription={{ values: true }}>{({ values }) => {
                        const ascendingOrderError = getAscendingOrderLimitError({ values });
                        const message = t('playtech.mobile.system.tooltip.limits.ascending.warning');

                        if (!ascendingOrderError) {
                            return null;
                        }

                        return <WarningMessage classNames={style} warning={message} />;
                    }}</FormSpy>
                    {submitError && <ErrorMessage error={submitError} animate={true} />}

                    {isLimitActionHidden
                        ? <input type="hidden" name="action" />
                        : (
                            <Select
                                name="action"
                                className={style.control}
                                label={t('label.action')}
                                placeholder={t('label.action')}
                                aria-label={t(['label.action', 'playtech.mobile.system.select.aria-label'])}
                                options={limitActions.map(actionName => ({
                                    value: actionName,
                                    label: t(`label.action.${actionName}`)
                                }))}
                            />
                        )
                    }

                    {availablePeriods.map(period => (
                        <EditableDropDown
                            key={period}
                            name={period}
                            mode={mode}
                            hint={
                                actualLimits[period] &&
                                <Hint
                                    tKey={`${tkPrefix}.hint.til`}
                                    date={actualLimits[period].resetDate}
                                />
                            }
                            label={t(`label.${period}`)}
                            dataList={map(parseDataList, dataList)}
                            className={style.control}
                            inputMode="decimal"
                            placeholder={t(`placeholder.${period}`)}
                            displayValuesMap={map(t, displayValuesMap)}
                            currencySymbol={formatter.getCurrencySymbol(currencyCode)}
                            aria-label={t([`label.${period}`, `placeholder.${period}`])}
                        />))
                    }

                    {hasPendingLimits && <h2 className={style.title}>{t('title.pending')}</h2>}

                    <PendingLimits
                        limitType={limitType}
                        limits={parsedPendingLimits}
                    />
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        className={style.submit}
                        disabled={submitting || pristine && isUserLimitActionAllowed}
                    >
                        {t('button.submit')}
                    </Button>
                </form>
            )}
        />
    );
};
